import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, from, EMPTY } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  tap,
  withLatestFrom,
  concatMap,
  mergeMap,
} from 'rxjs/operators';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Router } from '@angular/router';
import OktaSignIn from '@okta/okta-signin-widget';

import { AuthActions } from './auth.actions';
import { AuthService } from '../auth.service';
import { SessionTimerService } from '@timeout/session-timer.service';
import { WebSocketActions } from '@websocket/redux/websocket.actions';
import { selectUser } from '@shared/store/selectors/user.selectors';
import environment from '@environments/environment';
import { SESSION_TOKEN } from '../custom-providers/logout-provider';
import config from '../../app.config';
import {
  setLoading,
  storeUserDetail,
} from '@shared/store/actions/user.actions';
import { LoadingService } from '@loader/spinner';
import { removeImpersonationFlag } from '@auth/custom-providers/impersonation-provider';
import { NewRelicService } from '@data-access/shared';

const DEFAULT_ORIGINAL_URI = window.location.origin;

@Injectable()
export class AuthEffects {
  private signIn: any;
  private readonly _oktaAuth: OktaAuth = inject(OKTA_AUTH);
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _sessionTimerService = inject(SessionTimerService);
  private readonly _session = inject(SESSION_TOKEN);
  private readonly _loading = inject(LoadingService);
  private readonly _newRelic = inject(NewRelicService);

  // Handle the local cleanup part of logout
  logoutRequested$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.logoutOktaCompleted),
      withLatestFrom(this._store.select(selectUser)),
      concatMap(([_, user]) => {
        // Clear ALL storage immediately
        window.localStorage.clear();
        window.sessionStorage.clear();

        // Clear all cookies
        this.clearAllCookies();

        // Stop timers
        this._sessionTimerService.stopIdleTimers();

        // Remove _session token
        this._session.remove();

        // Clear token manager explicitly
        this._oktaAuth.tokenManager.clear();

        // Unsubscribe from websocket notifications if needed
        if (user?.id) {
          this._store.dispatch(
            WebSocketActions.unSubscribeUserNotification({ userId: user.id }),
          );
        }

        return of(AuthActions.logoutLocalCleanupCompleted());
      }),
      catchError((error) => of(AuthActions.logoutFailed({ error }))),
    ),
  );

  // Handle ending impersonation only (without full logout)
  endImpersonation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.endImpersonationRequested),
      switchMap(() => {
        return this._authService.endImpersonationSesion().pipe(
          tap(() => removeImpersonationFlag()),
          map(() => AuthActions.impersonationEnded()),
          catchError((error) => {
            console.error('Failed to end impersonation _session:', error);
            // Even if API call fails, still clear local state
            removeImpersonationFlag();
            return of(AuthActions.impersonationFailed({ error }));
          }),
        );
      }),
    ),
  );

  // Handle Okta logout - using the SDK with correct parameters to end SSO
  logoutOkta$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.logoutRequested),
      switchMap(() => {
        return from(
          this._oktaAuth.signOut({
            revokeAccessToken: true,
            revokeRefreshToken: true,
            clearTokensBeforeRedirect: true,
            postLogoutRedirectUri: environment.Okta.postLogoutRedirectUri,
          }),
        ).pipe(
          tap(() => {
            this._oktaAuth.tokenManager.clear();
            // Even after successful signout, clear again for good measure
            window.localStorage.clear();
            window.sessionStorage.clear();
            this.clearAllCookies();
          }),
          map(() => AuthActions.logoutOktaCompleted()),
          catchError((error) => {
            console.error('Error in Okta signOut:', error);

            // Even on error, clear local storage and do a manual redirect
            this._oktaAuth.tokenManager.clear();
            window.localStorage.clear();
            window.sessionStorage.clear();
            this.clearAllCookies();

            return of(AuthActions.logoutFailed({ error }));
          }),
        );
      }),
    ),
  );

  // Handle logout failures
  logoutFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.logoutFailed),
        tap(({ error }) => {
          console.error('Logout failed:', error, 'Redirecting to clinsphere');

          // Clear everything anyway
          window.localStorage.clear();
          window.sessionStorage.clear();
          this.clearAllCookies();
          this._oktaAuth.tokenManager.clear();

          // Direct redirect to login
          window.location.href = environment.clinsphereUrl;
        }),
      ),
    { dispatch: false },
  );

  // Initialize login process
  initializeLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.initializeLogin),
      tap(() => {
        console.log('Initializing login process');
        this._session.set();

        // Always clear token manager and storage at the start of login
        this._oktaAuth.tokenManager.clear();
        window.localStorage.removeItem('okta-token-storage');
        window.sessionStorage.removeItem('okta-token-storage');
      }),
      switchMap(() => {
        // Initialize Okta SignIn widget
        try {
          this.signIn = new OktaSignIn({
            issuer: environment.Okta.issuer,
            clientId: environment.Okta.clientId,
            redirectUri: environment.Okta.redirectUri,
            logo: 'assets/logo-wcg.svg',
            i18n: {
              en: {
                'primaryauth.title': 'Sign in to WCG',
              },
            },
            useClassicEngine: true,
          });
        } catch (e) {
          console.error('Error initializing Okta SignIn widget:', e);
          return of(AuthActions.loginFailed({ error: e }));
        }

        // Always force a fresh login regardless of authentication state
        return of(AuthActions.showOktaSignInWidget());
      }),
    ),
  );

  // Show Okta SignIn widget
  showOktaSignInWidget$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.showOktaSignInWidget),
      switchMap(() => {
        console.log('Showing Okta SignIn widget');
        // Set original URI
        const originalUri = this._oktaAuth.getOriginalUri();
        if (!originalUri || originalUri === DEFAULT_ORIGINAL_URI) {
          this._oktaAuth.setOriginalUri('/');
        }

        // Show widget
        return from(
          this.signIn.showSignInAndRedirect({
            el: '#okta-signin-container',
            scopes: config.oidc.scopes,
          }),
        ).pipe(
          tap(() => {
            // Remove the widget after showing
            this.signIn.remove();
          }),
          mergeMap(() => {
            if (this._oktaAuth.isLoginRedirect()) {
              // Callback flow - handle redirect
              return of(AuthActions.handleLoginRedirect());
            }
            // Normal app flow
            return EMPTY;
          }),
          catchError((err) => {
            console.error('Error showing SignIn widget:', err);
            this._newRelic.trackEvent('login_error_catch', { error: err });
            return of(AuthActions.loginFailed({ error: err }));
          }),
        );
      }),
    ),
  );

  // Token exchange already happened in OktaLoginCallbackGuard; this only loads user data.
  handleLoginRedirect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.handleLoginRedirect),
      tap(() => {
        this._loading.showLoading();
      }),
      switchMap(() =>
        from(this.handleUserData()).pipe(
          map(() => {
            this._loading.hideLoading();
            return AuthActions.loginSuccess();
          }),
          catchError((error) => {
            console.error('Error handling redirect:', error);
            this._loading.hideLoading();
            this._newRelic.trackEvent('login_error', { error });
            return of(AuthActions.loginFailed({ error }));
          }),
        ),
      ),
    ),
  );

  // Handle login failures
  loginFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthActions.loginFailed),
        tap(({ error }) => {
          console.error('Login failed:', error, 'Redirecting to clinsphere');
          // Clinsphere handles auth, we do not need to navigate to logout and explicitly destroy the _session
          window.location.href = environment.clinsphereUrl;
        }),
      ),
    { dispatch: false },
  );

  // Helper method to clear all cookies
  private clearAllCookies(): void {
    document.cookie.split(';').forEach((cookie) => {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;httpOnly`;
    });

    // Also try to clear Okta-specific cookies
    const oktaDomains = [
      window.location.hostname,
      `.${window.location.hostname}`,
      environment.Okta.issuer ? new URL(environment.Okta.issuer).hostname : '',
    ];

    const oktaCookies = [
      'okta-oauth-nonce',
      'okta-oauth-state',
      'okta_at',
      'okta_rt',
      'oktaStateToken',
      'JSESSIONID',
    ];

    oktaDomains.forEach((domain) => {
      if (domain) {
        oktaCookies.forEach((cookieName) => {
          document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain};secure;httpOnly`;
        });
      }
    });
  }

  // Helper to handle the user data after authentication
  private async handleUserData() {
    this._store.dispatch(setLoading({ loading: true }));

    try {
      const idToken = await this._oktaAuth.tokenManager.get('idToken');
      const oktaUser = (idToken as { claims: { name: string; email: string } })
        ?.claims;

      let nameParts = oktaUser.name.split(' ');

      if (!nameParts.length) {
        nameParts = ['', ''];
      } else if (nameParts.length === 1) {
        nameParts = [nameParts[0], ''];
      }

      if (!oktaUser.name || !oktaUser.email) {
        const user = {
          id: '',
          email: oktaUser.email as string,
          is_internal: false,
          first_name: nameParts[0],
          last_name: nameParts[nameParts.length - 1],
          data: {
            isProfileComplete: false,
            isReturnUser: false,
            showWelcomeCard: false,
            showSubmissionCard: false,
          },
          role: {
            id: '',
            name: '',
          },
        };

        this._store.dispatch(storeUserDetail({ user }));
        this._router.navigate(['/welcome']);
        return;
      }
    } catch (error) {
      console.error('Error case for revoked issue:', error);
      throw error;
    }
  }
}
